import React, { FC, useState } from 'react';
import TwDialog from '../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import TwDropzone, { TwDropzoneFiletypes } from './../../components/TwDropzone/TwDropzone';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import LoadsService from '../../providers/Loads/Loads.service';
import GuestService from '../../providers/Guest/Guest.service';
import { Backdrop, CircularProgress, Theme, Typography, useMediaQuery } from '@mui/material';
import TwFile from '../../components/TwFile/TwFile';
import TwDocumentViewer, {
  TwDocumentType
} from '../../components/TwDocumentViewer/TwDocumentViewer';
import { LoadDocumentParams, LoadDocumentType } from '../../redux/api/load/load.model';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import {
  useUploadOrderTripDocumentByTokenMutation,
  useUploadOrderTripDocumentMutation
} from '../../redux/api/orders/ordersApi';
import { OrdersTripsDocumentParams } from '../../redux/api/orders/orders.model';

interface UploadBolProps {
  loadId: string;
  open: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  guestServiceToken?: string;
  customUploadHandler?: () => void;
}

const MAX_FILE_SIZE = 104857600;

const ALLOWED_FILE_TYPES: TwDropzoneFiletypes[] = ['image/jpeg', 'image/png', 'application/pdf'];

const UploadBol: FC<UploadBolProps> = ({
  loadId,
  open,
  onClose,
  onSuccess,
  guestServiceToken,
  customUploadHandler
}) => {
  const {
    data: { userData }
  } = useAuth();

  const [files, setFiles] = useState<OrdersTripsDocumentParams[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [selectedFileToPreview, setSelectedFileToPreview] = useState<Blob | undefined>(undefined);
  const [selectedFileToPreviewTitle, setSelectedFileToPreviewTitle] = useState<string | undefined>(
    undefined
  );
  const { addNotification } = useNotifications();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();
  const [uploadOrderTripDocumentByToken] = useUploadOrderTripDocumentByTokenMutation();

  const handleClose = (): void => {
    clearFiles();
    onClose && onClose();
  };

  const clearFiles = (): void => {
    setFiles([]);
  };

  const handleSave = (): void => {
    setIsLoading(true);
    if (files && files.length > 0) {
      Promise.all(
        files.map((doc) =>
          !!guestServiceToken
            ? uploadOrderTripDocumentByToken({ document: doc, token: guestServiceToken }).unwrap()
            : uploadOrderTripDocument({
                document: doc
              }).unwrap()
        )
      )
        .then(() => {
          clearFiles();
          onSuccess && onSuccess();
          onClose && onClose();
        })
        .catch(() => {
          logRocketTrack('Upload document', {
            action: 'Upload BOL',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error uploading POD',
            variant: 'error'
          });
        })

        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleFileSelectErrors = (errors: string[]): void => {
    if (errors.length > 0)
      addNotification({
        message: errors[0],
        variant: 'error'
      });
  };

  const handleFilesUpload = (files: File[]): void => {
    files?.map((file: File) => {
      setFiles((prev) => [
        ...prev,
        {
          document: file,
          type: LoadDocumentType.proofOfDelivery,
          visibility: true,
          entity: 'order',
          orderId: loadId,
          tripId: '',
          name: file.name
        }
      ]);
    });
  };

  const closeFilePreview = (): void => {
    setSelectedFileToPreview(undefined);
    setDocViewOpen(false);
  };

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 10000
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <TwDialog
        title="POD is missing"
        open={open}
        onClickClose={handleClose}
        actions={[
          <TwButton key="1" type={TwButtonType.secondary} label="Skip" action={handleClose} />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label="Upload"
            action={handleSave}
            disabled={files.length == 0 || isLoading}
          />
        ]}
      >
        <Typography variant="h5" fontWeight="400" fontSize="14px" color="#6B7280">
          Please provide Proof of Delivery related to the load.
        </Typography>

        {customUploadHandler ? (
          <TwButton
            type={TwButtonType.secondary}
            label={'Upload document'}
            action={customUploadHandler}
            fullwidth={true}
          />
        ) : (
          <>
            <TwDropzone
              maxFiles={1}
              maxSize={MAX_FILE_SIZE}
              acceptedFiletypes={ALLOWED_FILE_TYPES}
              onFileUploaded={(files: File[]): void => {
                handleFilesUpload(files);
              }}
              onErrors={handleFileSelectErrors}
              text={matchesMobileOrTablet ? '' : 'Drag and drop or '}
              highlightedText={`${matchesMobileOrTablet ? 'C' : 'c'}lick to upload`}
              secondaryText="Files Supported: PDF, JPG, PNG maximum size 100MB"
            />
            {files &&
              files.length > 0 &&
              files.map((file, i) => {
                return (
                  <div key={file.name} style={{ cursor: 'pointer' }}>
                    <div>
                      <TwFile
                        name={`${i}-${file.name}`}
                        file={file.document}
                        clearInputAction={(): void => {
                          const newFileList = files.filter((item) => item.name !== file.name);

                          setFiles(newFileList);
                        }}
                        selectValue={''}
                        handleSelectChange={(): void => undefined}
                        optionsSelectList={[]}
                        showTypeSelect={false}
                        showVisibilitySelect={false}
                        onClick={(): void => {
                          setDocViewOpen(true);
                          setSelectedFileToPreview(file.document);
                          setSelectedFileToPreviewTitle(file.name);
                        }}
                        visibilityValue={true}
                        handleVisibilityChange={(e): void => undefined}
                      />
                    </div>
                  </div>
                );
              })}
          </>
        )}
      </TwDialog>

      <TwDocumentViewer
        readableId={0}
        selectedFileId={undefined}
        files={[]}
        blob={selectedFileToPreview}
        blobTitle={selectedFileToPreviewTitle}
        blobType={
          selectedFileToPreview && selectedFileToPreview.type.includes('pdf')
            ? TwDocumentType.pdf
            : TwDocumentType.image
        }
        open={docViewOpen}
        onClickClose={closeFilePreview}
      />
    </>
  );
};

export default UploadBol;
